
<template>
  <nav aria-label="breadcrumb">
    <ol class="breadcrumb breadcrumb-chevron p-3 bg-body-tertiary rounded-3">
      <li class="breadcrumb-item"><RouterLink class="link-body-emphasis" to="/"><font-awesome-icon :icon="['fas', 'home']" /><span class="visually-hidden">Home</span></RouterLink></li>
      <li class="breadcrumb-item"><RouterLink class="link-body-emphasis fw-semibold text-decoration-none" :to="TranslationsService.i18nRoute({ name: 'label-templates' })">{{ $t('menu.label-templates') }}</RouterLink></li>
      <li class="breadcrumb-item active" aria-current="page">{{ $t('menu.label-template-edit') }}</li>
    </ol>
  </nav>

  <div v-if="labelTemplate" class="tw-flex tw-flex-row tw-h-max tw-w-max">
    <table class="tw-px-5 tw-mx-5 tw-h-fit ">
      <tbody>
      <tr >
        <td colspan="2">
          <button class="btn btn-primary btn-lg d-block mb-3" type="submit" @click="save()">
            <font-awesome-icon :icon="['fas', 'floppy-disk']" />
            {{ $t('page.labelTemplate.form.confirm') }}
          </button>
        </td>
      </tr>
      <tr>
        <td class="tw-py-3 tw-px-3">
          <label class="tw-text-l tw-font-semibold" for="code">{{ $t('field.generic.code') }}</label>
        </td>
        <td class="tw-py-3 tw-px-3">
          <input v-model="labelTemplate.code" type="text" class="form-control" size="25" maxlength="20" id="code" ref="code" aria-describedby="codeHelp">
        </td>
      </tr>
      <tr>
        <td class="tw-py-3 tw-px-3 tw-align-top">
          <label class="tw-text-l tw-font-semibold" for="name">{{ $t('page.labelTemplate.form.templateContent') }}</label>
        </td>
        <td class="tw-py-3 tw-px-3">
          <textarea v-model="labelTemplate.templateContent" type="text" class="form-control" cols="80" rows="20" id="address" ref="address" aria-describedby="templateContentHelp" />
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <span >{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">

import {useLabelTemplatesStore, useSessionStore, useSitesStore,} from "@/stores";
import {computed, onMounted, ref} from "vue";
import TranslationsService from "@/services/translations.ts";
import {toast, type ToastOptions} from "vue3-toastify";
import { useI18n } from "vue-i18n";
import {router} from "@/services";
import ILabelTemplateUpsertRequest from "@/models/requests/ILabelTemplateUpsertRequest";
import {storeToRefs} from "pinia";
import LabelTemplate from "@/models/frontend/LabelTemplate";

const props = defineProps({
  templateId: { type: Number, required: false }
});

const i18n = useI18n();

const labelTemplateStore = useLabelTemplatesStore();
const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);
const siteStore = useSitesStore();
const { sites } = storeToRefs(siteStore);
const currentTentant = computed(() => {
  return sites.value.find(x => x.id == selectedSiteId.value)?.tenant;
})

const labelTemplate = ref<LabelTemplate | undefined>();
const isSaving = ref(false);

onMounted(async () => {
  if (props.templateId) {
    labelTemplate.value = labelTemplateStore.getById(props.templateId);
  }

  labelTemplate.value ??= {} as LabelTemplate
})

const save = async () => {
  if (!labelTemplate.value) {
    return;
  }

  isSaving.value = true;

  const request : ILabelTemplateUpsertRequest = {
    tenantCode: labelTemplate.value!.tenant?.code
      ?? currentTentant.value!.code,
    templateCode: labelTemplate.value!.code,
    templateContent: labelTemplate.value!.templateContent
  } as ILabelTemplateUpsertRequest;

  if (props.templateId) {
    await labelTemplateStore.update(labelTemplate.value.id, request);
  } else {
    await labelTemplateStore.create(request);
  }

  if (labelTemplateStore.error) {
    toast.error(i18n.t('page.labelTemplate.saveError', { error: labelTemplateStore.error }), {theme: 'colored'});
    isSaving.value = false;
  } else {
    toast.info(i18n.t('page.labelTemplate.saved'), { theme: 'colored', autoClose: 800 } as ToastOptions);

    setTimeout(() => {
      router.push({ name: 'label-templates' });
      isSaving.value = false;
    }, 1000);
  }
};
</script>

<style scoped>

</style>